<script lang="ts" setup>
import { useAsyncData } from '@/home/hooks/async-data';
import { $fetch } from '@/home/plugins/fetch';

const { data: categories } = useAsyncData<any[], Record<string, any>>(
  'certification:certifications',
  () => $fetch('/certification/v1/list'),
  {
    default: () => [],
  },
);

const { data: partners } = useAsyncData<any[], Record<string, any>>(
  'certification:partners',
  () => $fetch('/certification/v1/partner/list'),
  {
    default: () => [],
  },
);
</script>

<template>
  <main class="page-certification pb-80">
    <section class="banner h-400">
      <div class="container pt-146 text-#2d2d2d">
        <h1 class="mt-0 h-70 text-50 font-medium leading-70">{{ $t('certification.index.title') }}</h1>
      </div>
    </section>

    <section class="container">
      <div class="mt-48" v-for="category in categories" :key="category.categoryId">
        <h2 class="m-0 text-center text-32 text-#2d2d2d leading-48 leading-48">{{ category.categoryName }}</h2>
        <p class="m-0 mt-8 text-center text-16 text-#2d2d2d leading-24">{{ category.description }}</p>

        <ul class="m-0 mt-32 flex flex-wrap list-none gap-32 p-0 <2xl:gap-24">
          <li
            class="card w-432 border-1 border-[rgba(154,158,166,0.1)] rounded-4 border-solid bg-white px-24 pb-32 pt-24 shadow-[0_0_16px_0_rgba(0,0,0,0.04)] transition-transform <2xl:w-384 hover:translate-y--12px <2xl:hover:translate-y--6px"
            v-for="item in category.list" :key="item.certificationId"
          >
            <div class="mx-auto size-112">
              <img class="lazyload size-full rounded-50% object-cover" :data-src="item.logo" :alt="item.name">
            </div>

            <h3 class="m-0 mt-16 truncate text-center text-24 text-#3d3d3d font-medium leading-36" :title="item.name">
              {{ item.name }}
            </h3>
            <p class="line-clamp-5 m-0 mt-8 h-120 text-justify text-16 text-#3d3d3d leading-24" :title="item.description">
              {{ item.description }}
            </p>

            <div class="flex justify-center">
              <template v-if="item.link">
                <a
                  :href="item.link"
                  class="mt-24 block border-1 border-#1180ff rounded-4 border-solid bg-transparent px-24 py-4 text-center text-center text-16 text-#1180ff leading-24 no-underline hover:bg-#1180ff hover:text-white"
                  target="_blank"
                >
                  {{ $t('certification.index.more') }}
                </a>
              </template>
              <template v-else>
                <RouterLink
                  :to="`/certification/detail/${item.certificationId}`"
                  class="mt-24 block border-1 border-#1180ff rounded-4 border-solid bg-transparent px-24 py-4 text-center text-center text-16 text-#1180ff leading-24 no-underline hover:bg-#1180ff hover:text-white"
                >
                  {{ $t('certification.index.more') }}
                </RouterLink>
              </template>
            </div>
          </li>
        </ul>
      </div>
    </section>

    <section class="container mt-48" v-if="partners?.length">
      <h2 class="m-0 text-center text-32 text-#2d2d2d leading-48 leading-48">
        {{ $t('certification.index.partner') }}
      </h2>

      <div>
        <template v-for="partner in partners" :key="partner.certificationId">
          <div class="partners mt-48 first:mt-32" v-if="partner?.list?.length">
            <h4 class="relative m-0 flex items-center justify-between text-center text-24 font-normal leading-48 after:block after:h-2 after:w-full after:opacity-20 after:content-empty before:block before:h-2 before:w-full before:opacity-20 before:content-empty">
              <span class="relative z-1 inline-block flex-shrink-0 px-32">{{ partner.certificationName }}</span>
            </h4>

            <ul class="m-0 mt-24 flex flex-shrink flex-wrap list-none gap-x-16 gap-y-16 p-0 <2xl:gap-x-32 <2xl:gap-y-16" :class="{ 'justify-center': partner.list?.length < 3 }">
              <template v-for="item in partner.list" :key="item.partnerId">
                <li class="h-78 w-213 overflow-hidden bg-white" v-if="item.logo">
                  <img :data-src="item.logo" class="lazyload block size-full object-contain transition-all hover:ml--5% hover:mt--2% hover:size-110%" :alt="item.partnerName" :title="item.partnerName">
                </li>
              </template>
            </ul>
          </div>
        </template>
      </div>
    </section>
  </main>
</template>

<style lang="scss" scoped>
.page-certification {
  * {
    box-sizing: border-box;
  }

  .banner {
    background-color: #f4f6f7;
    background-image: url(@/home/assets/certification/banner.jpg);
    background-repeat: no-repeat;
    background-position: center;
    background-size: 1920px auto;
  }

  .card {
    background-image: url(@/home/assets/certification/card-bg.jpg);
    background-repeat: no-repeat;
    background-position: left top;
    background-size: 100% auto;
  }

  .partners {
    h4 {
      &::before {
        background: linear-gradient(116.01deg, rgba(91, 230, 255, 0) 0%, rgba(17, 128, 255, 0.5) 100%);
      }

      &::after {
        background: linear-gradient(243.99deg, rgba(91, 230, 255, 0) 0%, rgba(17, 128, 255, 0.5) 100%);
      }
    }
  }
}
</style>
